import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Clock,
  Plane,
  TrendingUp,
  Wrench,
  Activity,
  Calendar,
  ChevronRight,
  CalendarClock,
  ArrowRight,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import api from '@/services/api'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts'

interface AircraftHealth {
  tailNumber: string
  aircraftType: string
  healthScore: number
  flightHours: number
  cycles: number
  nextScheduledMaint: string
  status: 'healthy' | 'attention' | 'critical'
}

interface PredictedFailure {
  id: string
  tailNumber: string
  component: string
  ataChapter: string
  partNumber: string
  probability: number
  timeToFailure: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  recommendedAction: string
  estimatedCost: number
}

interface MaintenanceAction {
  id: string
  tailNumber: string
  action: string
  dueDate: string
  priority: 'routine' | 'priority' | 'urgent'
  ataChapter: string
  estimatedTime: string
}

// Sample fleet health data — HondaJet HA-420 fleet
const fleetHealth: AircraftHealth[] = [
  {
    tailNumber: 'N420HJ',
    aircraftType: 'HA-420 Elite S',
    healthScore: 94,
    flightHours: 487,
    cycles: 612,
    nextScheduledMaint: '2026-04-10',
    status: 'healthy',
  },
  {
    tailNumber: 'N421HA',
    aircraftType: 'HA-420 Elite II',
    healthScore: 78,
    flightHours: 143,
    cycles: 198,
    nextScheduledMaint: '2026-03-05',
    status: 'attention',
  },
  {
    tailNumber: 'N789HJ',
    aircraftType: 'HA-420 Elite S',
    healthScore: 52,
    flightHours: 605,
    cycles: 740,
    nextScheduledMaint: '2026-02-22',
    status: 'critical',
  },
]

// Sample predicted failures — HA-420 specific components
const predictedFailures: PredictedFailure[] = [
  {
    id: '1',
    tailNumber: 'N789HJ',
    component: 'GE Honda HF120 - HPT Blade Erosion (L)',
    ataChapter: 'ATA 72-50',
    partNumber: 'HF120-72-050',
    probability: 82,
    timeToFailure: '~120 flight hours',
    severity: 'critical',
    recommendedAction: 'Borescope inspection at next Phase 2; plan HPT module swap if erosion confirmed',
    estimatedCost: 34500,
  },
  {
    id: '2',
    tailNumber: 'N421HA',
    component: 'Hydraulic Pump Pressure Switch',
    ataChapter: 'ATA 29-10',
    partNumber: 'HA-420-29-010',
    probability: 68,
    timeToFailure: '~250 flight hours',
    severity: 'high',
    recommendedAction: 'Replace during Phase 3 landing gear / hydraulics inspection',
    estimatedCost: 8200,
  },
  {
    id: '3',
    tailNumber: 'N420HJ',
    component: 'Main Landing Gear Brake Wear',
    ataChapter: 'ATA 32-40',
    partNumber: 'HA-420-32-050',
    probability: 61,
    timeToFailure: '~400 flight hours',
    severity: 'medium',
    recommendedAction: 'Monitor wear pin indicators; plan brake replacement at Phase 3',
    estimatedCost: 6800,
  },
  {
    id: '4',
    tailNumber: 'N421HA',
    component: 'Garmin GDU 1060 Display Backlight',
    ataChapter: 'ATA 34',
    partNumber: 'GDU-1060-BL',
    probability: 45,
    timeToFailure: '~800 flight hours',
    severity: 'medium',
    recommendedAction: 'Include backlight module in next Phase 4 avionics inspection',
    estimatedCost: 4200,
  },
]

// Sample maintenance actions — HA-420 phased program
const maintenanceActions: MaintenanceAction[] = [
  {
    id: '1',
    tailNumber: 'N789HJ',
    action: 'Phase 2 Inspection (Overdue) — HF120 Engine & Fuel',
    dueDate: '2026-02-22',
    priority: 'urgent',
    ataChapter: 'ATA 72/73/28',
    estimatedTime: '56 hours',
  },
  {
    id: '2',
    tailNumber: 'N421HA',
    action: 'Phase 2 Inspection — HF120 Engine & Fuel System',
    dueDate: '2026-03-05',
    priority: 'priority',
    ataChapter: 'ATA 72/28',
    estimatedTime: '56 hours',
  },
  {
    id: '3',
    tailNumber: 'N420HJ',
    action: 'Phase 1 — 600-Hr Major Inspection',
    dueDate: '2026-04-10',
    priority: 'routine',
    ataChapter: 'ATA 05',
    estimatedTime: '200 hours (all phases)',
  },
]

// Health trend data — HA-420 fleet
const healthTrendData = [
  { date: 'Jan 1', N420HJ: 96, N421HA: 85, N789HJ: 72 },
  { date: 'Jan 8', N420HJ: 96, N421HA: 83, N789HJ: 68 },
  { date: 'Jan 15', N420HJ: 95, N421HA: 81, N789HJ: 62 },
  { date: 'Jan 22', N420HJ: 95, N421HA: 79, N789HJ: 57 },
  { date: 'Jan 29', N420HJ: 94, N421HA: 78, N789HJ: 54 },
  { date: 'Feb 4', N420HJ: 94, N421HA: 78, N789HJ: 52 },
]

interface PhaseSchedule {
  tail_number: string
  aircraft_model: string
  current_hours: number
  next_phase: number
  hours_until_next_phase: number
  urgency: string
}

export function PredictiveMaintenancePage() {
  const [phaseSchedule, setPhaseSchedule] = useState<PhaseSchedule[]>([])

  useEffect(() => {
    api.get<{ schedule: PhaseSchedule[] }>('/api/maintenance-program/schedule')
      .then((data) => setPhaseSchedule(data.schedule || []))
      .catch(() => {})
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'attention':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'priority':
        return 'bg-orange-100 text-orange-800'
      case 'routine':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getHealthScoreColor = (score: number) => {
    if (score >= 85) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Predictive Maintenance</h1>
          <p className="text-gray-600">AI-powered failure predictions and maintenance planning</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Activity className="h-3 w-3" />
            Live Monitoring
          </Badge>
          <Button>
            <Calendar className="mr-2 h-4 w-4" />
            Schedule Maintenance
          </Button>
        </div>
      </div>

      {/* Fleet Health Overview */}
      <div className="grid gap-4 lg:grid-cols-3">
        {fleetHealth.map((aircraft) => (
          <Card key={aircraft.tailNumber} className={`border-2 ${getStatusColor(aircraft.status)}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plane className="h-5 w-5" />
                  <CardTitle className="text-lg">{aircraft.tailNumber}</CardTitle>
                </div>
                <Badge variant="secondary">{aircraft.aircraftType}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Health Score</span>
                  <span className={`text-2xl font-bold ${getHealthScoreColor(aircraft.healthScore)}`}>
                    {aircraft.healthScore}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      aircraft.healthScore >= 85
                        ? 'bg-green-500'
                        : aircraft.healthScore >= 60
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${aircraft.healthScore}%` }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Flight Hours</span>
                    <p className="font-medium">{aircraft.flightHours.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Cycles</span>
                    <p className="font-medium">{aircraft.cycles.toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Next Scheduled</span>
                  <span className="font-medium">{aircraft.nextScheduledMaint}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Health Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Fleet Health Trend
          </CardTitle>
          <CardDescription>Health score progression over the last 6 weeks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={healthTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="N420HJ"
                  stackId="1"
                  stroke="#22c55e"
                  fill="#dcfce7"
                  name="N420HJ (Elite S)"
                />
                <Area
                  type="monotone"
                  dataKey="N421HA"
                  stackId="2"
                  stroke="#eab308"
                  fill="#fef9c3"
                  name="N421HA (Elite II)"
                />
                <Area
                  type="monotone"
                  dataKey="N789HJ"
                  stackId="3"
                  stroke="#ef4444"
                  fill="#fee2e2"
                  name="N789HJ (Elite S)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Predicted Failures */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Predicted Failures
            </CardTitle>
            <CardDescription>Components requiring attention based on AI analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {predictedFailures.map((failure) => (
                <div
                  key={failure.id}
                  className={`rounded-lg border p-4 ${getSeverityColor(failure.severity)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{failure.component}</span>
                        <Badge variant="outline" className="text-xs">
                          {failure.tailNumber}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">{failure.ataChapter} • P/N {failure.partNumber}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">{failure.probability}%</div>
                      <div className="text-xs text-gray-500">probability</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Clock className="h-4 w-4" />
                      {failure.timeToFailure}
                    </div>
                    <div className="font-medium">${failure.estimatedCost.toLocaleString()}</div>
                  </div>
                  <p className="mt-2 text-sm">{failure.recommendedAction}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recommended Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-blue-500" />
              Recommended Actions
            </CardTitle>
            <CardDescription>Prioritized maintenance tasks based on predictions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {maintenanceActions.map((action) => (
                <div key={action.id} className="rounded-lg border bg-white p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{action.action}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Badge variant="outline">{action.tailNumber}</Badge>
                        <span>{action.ataChapter}</span>
                      </div>
                    </div>
                    <Badge className={getPriorityColor(action.priority)}>
                      {action.priority}
                    </Badge>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Calendar className="h-4 w-4" />
                        Due: {action.dueDate}
                      </div>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Clock className="h-4 w-4" />
                        Est: {action.estimatedTime}
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      Schedule
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase-Based Predictions */}
      {phaseSchedule.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-blue-500" />
                Phase-Based Predictions
              </CardTitle>
              <CardDescription>
                ALIM phased maintenance schedule — hours until next phase inspection
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/app/maintenance-program">
                View Full Program
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {phaseSchedule.slice(0, 6).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex items-center gap-3">
                    <Plane className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium">{item.tail_number}</p>
                      <p className="text-xs text-gray-500">{item.aircraft_model}</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Current: {item.current_hours.toFixed(0)} hrs</p>
                  </div>
                  <div className="text-center">
                    <Badge variant="outline">Phase {item.next_phase}</Badge>
                  </div>
                  <div className="text-right">
                    <Badge variant={
                      item.urgency === 'critical' ? 'destructive' :
                      item.urgency === 'high' ? 'warning' : 'secondary'
                    }>
                      {item.hours_until_next_phase.toFixed(0)} hrs remaining
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Component Risk Matrix */}
      <Card>
        <CardHeader>
          <CardTitle>Component Risk Indicators</CardTitle>
          <CardDescription>System-level risk assessment across fleet</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            {[
              { system: 'GE Honda HF120 (ATA 72)', risk: 'medium', score: 76 },
              { system: 'Hydraulics (ATA 29)', risk: 'medium', score: 72 },
              { system: 'Landing Gear (ATA 32)', risk: 'low', score: 91 },
              { system: 'Garmin G3000 Avionics (ATA 34)', risk: 'low', score: 94 },
              { system: 'Fuel System (ATA 28)', risk: 'low', score: 89 },
              { system: 'Flight Controls (ATA 27)', risk: 'low', score: 93 },
              { system: 'Electrical Power (ATA 24)', risk: 'low', score: 88 },
              { system: 'Air Conditioning (ATA 21)', risk: 'low', score: 90 },
            ].map((component) => (
              <div
                key={component.system}
                className={`rounded-lg border p-4 ${
                  component.risk === 'critical'
                    ? 'border-red-200 bg-red-50'
                    : component.risk === 'medium'
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-green-200 bg-green-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{component.system}</span>
                  <span
                    className={`text-lg font-bold ${
                      component.risk === 'critical'
                        ? 'text-red-600'
                        : component.risk === 'medium'
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}
                  >
                    {component.score}%
                  </span>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
                  <div
                    className={`h-1.5 rounded-full ${
                      component.risk === 'critical'
                        ? 'bg-red-500'
                        : component.risk === 'medium'
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${component.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
